/**
 * 视频首帧抽取工具。
 *
 * 用 <video> + canvas 抽取第一帧作为封面图，适用于任意可访问的视频 URL。
 * 远程视频需支持 CORS（crossOrigin="anonymous"），否则 canvas 会被污染导致 toBlob 失败。
 */

/** 抽取视频第一帧，返回 webp Blob。 */
export function captureFirstFrame(
  videoUrl: string,
  options?: { seekSec?: number; maxWidth?: number },
): Promise<Blob> {
  const seekSec = options?.seekSec ?? 0.1
  const maxWidth = options?.maxWidth ?? 480

  return new Promise<Blob>((resolve, reject) => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.preload = 'metadata'
    video.muted = true
    video.src = videoUrl

    let settled = false

    const cleanup = () => {
      video.removeAttribute('src')
      video.load()
    }

    const onLoadedMetadata = () => {
      // 跳到指定时间点（避开纯黑首帧）
      video.currentTime = Math.min(seekSec, Math.max(0, (video.duration || 1) - 0.05))
    }

    const onSeeked = () => {
      try {
        const vw = video.videoWidth || 1
        const vh = video.videoHeight || 1
        const scale = Math.min(1, maxWidth / Math.max(vw, vh))
        const width = Math.round(vw * scale)
        const height = Math.round(vh * scale)

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          throw new Error('Failed to get canvas context')
        }

        ctx.drawImage(video, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (settled) return
            settled = true
            cleanup()
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create cover blob'))
            }
          },
          'image/webp',
          0.7,
        )
      } catch (err) {
        if (settled) return
        settled = true
        cleanup()
        reject(err instanceof Error ? err : new Error('captureFirstFrame failed'))
      }
    }

    const onError = () => {
      if (settled) return
      settled = true
      cleanup()
      reject(new Error('Failed to load video for cover extraction'))
    }

    video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true })
    video.addEventListener('seeked', onSeeked, { once: true })
    video.addEventListener('error', onError, { once: true })

    // 超时兜底（10 秒）
    setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      reject(new Error('Cover extraction timed out'))
    }, 10_000)
  })
}
